import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ROLE_LABEL = {
  committee_viewer: "Committee Viewer",
  investment_professional: "Investment Professional",
};

export default function JoinTeam() {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Join Your Team | ImpactVest";
    (async () => {
      try {
        const res = await api.get(`/team-links/preview/${token}`);
        if (res.data.valid) {
          setLink(res.data);
          setState("valid");
        } else {
          setState("invalid");
        }
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 12) return toast.error("Password must be at least 12 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords do not match.");
    setSubmitting(true);
    try {
      const res = await api.post("/team-links/accept", {
        link_token: token,
        full_name: form.full_name,
        email: form.email,
        password: form.password,
      });
      login(res.data.token, res.data.user);
      toast.success("Welcome to ImpactVest.");
      window.location.assign("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to join.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="pt-28 md:pt-32 pb-24 min-h-[70vh]">
        <div className="iv-container max-w-2xl">
          <div className="iv-eyebrow mb-8">Invite Your Team</div>
          {state === "loading" && (
            <p className="font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Verifying link…</p>
          )}
          {state === "invalid" && (
            <>
              <h1 className="font-serif-display text-[40px] md:text-[52px] leading-tight text-iv-fg">
                This team link is no longer available.
              </h1>
              <p className="mt-6 text-[15px] text-iv-fg-2">
                It may have expired, been revoked, or reached its maximum number of uses. Please ask
                your organization administrator for a new link.
              </p>
              <div className="mt-8"><Link to="/login" className="iv-btn iv-btn--primary">Pilot Sign In</Link></div>
            </>
          )}
          {state === "valid" && (
            <>
              <h1 className="font-serif-display text-[40px] md:text-[52px] leading-tight text-iv-fg">
                Join {link.organization} on ImpactVest.
              </h1>
              <p className="mt-4 text-[15px] text-iv-fg-2">
                You&rsquo;ll be added as a <strong className="text-iv-fg">{ROLE_LABEL[link.default_role] || link.default_role}</strong>.
                {link.allowed_email_domain && link.require_domain_match
                  ? ` Please use your @${link.allowed_email_domain} email.`
                  : " Please verify your individual professional email."}
              </p>
              <p className="mt-2 font-mono text-[10.5px] uppercase tracking-widest text-iv-fg-3">
                {link.uses_remaining} place{link.uses_remaining === 1 ? "" : "s"} remaining
              </p>
              <form onSubmit={submit} className="mt-10 iv-panel p-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <label className="iv-field">
                    <span>Full Name</span>
                    <input required value={form.full_name} onChange={up("full_name")} placeholder="Your name" />
                  </label>
                  <label className="iv-field">
                    <span>Professional Email</span>
                    <input required type="email" value={form.email} onChange={up("email")} placeholder="name@organization.com" />
                  </label>
                  <label className="iv-field">
                    <span>Password (min 12 characters)</span>
                    <input required type="password" minLength={12} value={form.password} onChange={up("password")} placeholder="Choose a strong password" />
                  </label>
                  <label className="iv-field">
                    <span>Confirm Password</span>
                    <input required type="password" value={form.confirm} onChange={up("confirm")} placeholder="Re-enter password" />
                  </label>
                </div>
                <button type="submit" disabled={submitting} className="iv-btn iv-btn--primary mt-8">
                  {submitting ? "Joining…" : "Join Team"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
